import firebase from "firebase/compat";
import { cloneDeep } from "lodash";
import { convertDateStringToTimestamp } from "./new";
import { uploadFiles } from "utils/fileValidation";
import { uploadImages } from "utils/imageValidation";
import { FileOrImagePreview, ImagePreview } from "../../@types/listings/imageData";
import { typeIsOneOfListingTypes } from "./helper";
import type { Listing } from "../../@types/listings/listing";
import type { AdditionalUserWithFee, ListingContact } from "../../@types/listings/user";
import type {
  EditStatus,
  PriceHistory,
  NewListingData,
  EditableListing,
  AddressCoordinates,
  RequiredListingData
} from "../../@types/listings/new";
import type { FileOrFilePreview, FilePreview } from "../../@types/listings/fileData";
import { isRentedListing } from "../../@types/sharedSchema";
import type { ProcessedListingAddress, ProcessedListingData } from "../../@types/sharedSchema";

type CreateOperationProperties = {
  organizationId: string;
  /**
   * Runs once the listing document exists, with its id.
   *
   * ⚠ MAY RETURN A PROMISE, AND IT IS AWAITED — see the call site below. The
   * signature was `=> void`, so `NewListingDialog`'s handler (which resolves the
   * seller into a contact and links it, three sequential round trips) had its
   * promise dropped on the floor and `onSuccess` ran ~2-3 seconds early. Every
   * other caller is synchronous and is unaffected: awaiting a non-promise only
   * costs one microtask, and their ordering relative to `onSuccess` — before it,
   * as it has always been — is unchanged.
   *
   * `createdListing` is a picker-ready snapshot of the just-created listing so
   * callers (e.g. the signature send form, the listings board's optimistic claim
   * row) can use it immediately, before the mirror and the Algolia/Postgres index
   * catch up. Optional 3rd arg keeps existing callers (which only read
   * id + isExclusivitySet) source-compatible.
   */
  onListingCreation?: (
    id: string,
    isExclusivitySet: boolean,
    createdListing?: Partial<ProcessedListingData>
  ) => void | Promise<void>;
};

type UpdateOperationProperties = {
  listing: Listing;
  editStatus?: EditStatus;
  updatedOrganizationId?: string;
};

type OperationProperties = CreateOperationProperties | UpdateOperationProperties;

type CreateOrUpdateListing = {
  address: ProcessedListingAddress[];
  userEmail: string;
  ownersData: AdditionalUserWithFee[];
  selectedFiles: FileOrFilePreview[];
  operationProps: OperationProperties;
  selectedImages: FileOrImagePreview[];
  listingContacts: ListingContact[];
  buyerOriginData: AdditionalUserWithFee[];
  addressCoordinates: AddressCoordinates | null;
  editableFormColumns: EditableListing;
  onError: () => void;
  /**
   * The listing (and everything chained off its creation) is written.
   *
   * ⚠ ALSO AWAITED. The edit path's handler is `async` — it persists the seller
   * before closing the dialog — so without the await this function resolved while
   * that write was still in flight, and a caller could not tell "saved" from
   * "saving". Awaiting makes the returned promise honestly mean *done*, which is
   * what the dialog's submit guard keys on.
   */
  onSuccess: (pictures?: ImagePreview[]) => void | Promise<void>;
};

export async function createOrUpdateListing({
  address,
  userEmail,
  ownersData,
  selectedFiles,
  operationProps,
  selectedImages,
  listingContacts,
  buyerOriginData,
  addressCoordinates,
  editableFormColumns,
  onError,
  onSuccess
}: CreateOrUpdateListing): Promise<void> {
  const isCreateOperation = "organizationId" in operationProps;

  const copyOfEditableFormColumns = cloneDeep(editableFormColumns);

  const {
    fee,
    floor,
    status,
    signDate,
    hasGarden,
    hasParking,
    hasTerrace,
    floorOutOf,
    listingType,
    projectName,
    exclusivity,
    listingState,
    evictionDate,
    leaseEndDate,
    sizeOfPlotM2,
    leaseStartDate,
    sizeOfGardenM2,
    sizeOfTerraceM2,
    transactionType,
    sizeOfPropertyM2,
    feeIncludesTaxes,
    parkingSpotsCount
  } = copyOfEditableFormColumns;

  // Lease dates exist only on a RENTED listing (`isRentedListing`), judged on the status this
  // save leaves behind: the row menu's "הושכר" passes it as `editStatus` and the form state
  // still holds the old one. A listing that goes back on the market drops its dates here, so
  // it is never reminded about an agreement that is over.
  const statusAfterSave = ("editStatus" in operationProps && operationProps.editStatus) || status;
  const isRentedAfterSave = isRentedListing({ status: statusAfterSave, transactionType });

  const { street, neighborhood, city, coords } = address[0];

  const parsedAddressCoords = addressCoordinates ?? coords ?? null;

  const newOwners = ownersData.map(({ email }) => email);
  const newBuyerOrigin = buyerOriginData.map(({ email }) => email);

  const parsedBuyerOrigin = newBuyerOrigin.length ? newBuyerOrigin : null;
  const parsedBuyerOriginData = buyerOriginData.length ? buyerOriginData : null;

  const parsedProjectName = listingState === "newConstruction" ? projectName : "";
  const parsedFeeIncludesTaxes = fee ? feeIncludesTaxes : false;

  const parsedSizeOfGardenM2 = hasGarden ? sizeOfGardenM2 : null;
  const parsedSizeOfTerraceM2 = hasTerrace ? sizeOfTerraceM2 : null;
  const parsedParkingSpotsCount = hasParking ? parkingSpotsCount : null;

  let parsedListingContacts: ListingContact[] | null = null;

  if (listingContacts.length) {
    /**
     * ⚠ `contactId` MUST BE CARRIED. This map is a whitelist — that part is
     * deliberate, so a form-only field can never leak onto the document — but it
     * used to list only the three name/phone keys, so EVERY save silently
     * stripped the link between the listing's seller entry and the CRM contact.
     *
     * It looked harmless because `persistListingSeller` re-stamps the id right
     * afterwards (step 3, `linkListingContact`). But that step is reached only
     * when the seller has a usable phone: a seller recorded by NAME ALONE, or one
     * whose number cannot be parsed, returns `NO_RESULT` — so for those the link
     * was destroyed on every save and never restored, on a save the realtor made
     * about something else entirely.
     *
     * An empty string is normalized away rather than written: `linkListingContact`
     * clears a link by writing `''` (an array element's key cannot be
     * `FieldValue.delete()`d), and absent is the same statement in one spelling.
     */
    parsedListingContacts = listingContacts.map(
      ({ callerPhoneNumber, callerFirstName, callerLastName, contactId }) => ({
        callerPhoneNumber,
        callerFirstName,
        callerLastName,
        ...(contactId ? { contactId } : {})
      })
    );
  }

  let parsedSizeOfPropertyM2 = sizeOfPropertyM2;

  // If the listing type is "lot" we do not store sizeOfPropertyM2 value.
  if (typeIsOneOfListingTypes(listingType, ["lot"])) parsedSizeOfPropertyM2 = null;

  let parsedSizeOfPlotM2 = sizeOfPlotM2;

  // If the listing type is not one of "privateHouse", "semiDetached", "lot", and "warehouses", we do not store sizeOfPlotM2 value.
  if (
    !typeIsOneOfListingTypes(listingType, ["privateHouse", "semiDetached", "lot", "warehouses"])
  ) {
    parsedSizeOfPlotM2 = null;
  }

  let parsedFloor = floor;

  // If the listing type is "lot", we do not store both floor and floorOutOf values.
  if (typeIsOneOfListingTypes(listingType, ["lot"])) parsedFloor = null;

  let parsedFloorOutOf = typeof parsedFloor === "number" ? floorOutOf : null;

  // If the listing type is "warehouses" or "industrialBuildings", we do not store floorOutOf value.
  if (typeIsOneOfListingTypes(listingType, ["warehouses", "industrialBuildings"])) {
    parsedFloorOutOf = null;
  }

  let additionalData: NewListingData | RequiredListingData;

  const requiredData: RequiredListingData = {
    city: city,
    floor: parsedFloor,
    owners: newOwners,
    address: street,
    signDate: convertDateStringToTimestamp(signDate),
    ownersData: ownersData,
    floorOutOf: parsedFloorOutOf,
    projectName: parsedProjectName,
    buyerOrigin: parsedBuyerOrigin,
    exclusivity: convertDateStringToTimestamp(exclusivity),
    sizeOfPlotM2: parsedSizeOfPlotM2,
    neighborhood: neighborhood,
    evictionDate: convertDateStringToTimestamp(evictionDate),
    leaseEndDate: isRentedAfterSave ? convertDateStringToTimestamp(leaseEndDate) : null,
    leaseStartDate: isRentedAfterSave ? convertDateStringToTimestamp(leaseStartDate) : null,
    priceHistory: null,
    sizeOfGardenM2: parsedSizeOfGardenM2,
    listingContacts: parsedListingContacts,
    buyerOriginData: parsedBuyerOriginData,
    sizeOfTerraceM2: parsedSizeOfTerraceM2,
    sizeOfPropertyM2: parsedSizeOfPropertyM2,
    feeIncludesTaxes: parsedFeeIncludesTaxes,
    parkingSpotsCount: parsedParkingSpotsCount,
    addressCoordinates: parsedAddressCoords,
    pendingPriceUpdates: false
  };

  if (isCreateOperation) {
    additionalData = {
      ...requiredData,
      createdBy: userEmail,
      userFavorites: [],
      createdByOrganization: operationProps.organizationId
    };
  } else {
    const {
      price: listingPrice,
      createdBy,
      createdTime,
      priceHistory,
      pendingPriceUpdates
    } = operationProps.listing ?? {};

    let newPriceHistory: PriceHistory[] | null = priceHistory;

    const isPriceUpdated = listingPrice !== editableFormColumns.price;
    const isPendingPriceUpdated = pendingPriceUpdates || isPriceUpdated;

    if (isPriceUpdated) {
      const { price } = editableFormColumns;

      const newPriceHistoryItem: PriceHistory = {
        price: price,
        userMail: userEmail,
        updatedAt: new Date() as unknown as firebase.firestore.Timestamp
      };

      if (!priceHistory) {
        const oldPriceHistoryItem: PriceHistory = {
          price: listingPrice,
          userMail: createdBy,
          updatedAt: createdTime
        };
        newPriceHistory = [oldPriceHistoryItem, newPriceHistoryItem];
      } else newPriceHistory = [...priceHistory, newPriceHistoryItem];
    }

    additionalData = {
      ...requiredData,
      priceHistory: newPriceHistory,
      pendingPriceUpdates: isPendingPriceUpdated,
      ...(operationProps.updatedOrganizationId !== undefined && {
        createdByOrganization: operationProps.updatedOrganizationId
      })
    };
  }

  let files: null | FilePreview[] = null;
  let pictures: null | ImagePreview[] = null;

  try {
    files = await uploadFiles(selectedFiles, userEmail);
    pictures = await uploadImages(selectedImages, userEmail);

    let targetListing: EditableListing & RequiredListingData = {
      ...editableFormColumns,
      ...additionalData,
      files: files,
      pictures: pictures,
      // Normalize to null so Firestore never receives `undefined` for legacy
      // listings created before the video field existed.
      videoUrl: editableFormColumns.videoUrl?.trim() || null
    };

    if (isCreateOperation) {
      const { onListingCreation } = operationProps;

      const { id } = await firebase
        .firestore()
        .collection("real-estate-listings")
        .add(targetListing);

      const isExclusivitySet = !!exclusivity;

      // Picker-ready snapshot mapped from the CRM listing shape to
      // ProcessedListingData (what the signature listing picker consumes, and
      // what the listings board builds its optimistic just-claimed row from).
      // Marked listingOrigin:'webot' — manually-created listings are Webot-owned.
      //
      // `listingContacts` rides along for one reason: on a `webot` row the mirror
      // Cloud Function overwrites sellerName/sellerPhoneNumber with the OWNING
      // REALTOR's own details, so `resolveListingSeller` reads this field and
      // nothing else to answer "who is the owner". Without it the board's
      // substituted row would honestly but pointlessly report "no owner recorded"
      // for a listing on which she had just typed one.
      const createdListing: Partial<ProcessedListingData> = {
        id,
        address: address[0],
        price: editableFormColumns.price,
        roomsCount: editableFormColumns.rooms ?? undefined,
        squareMeter: editableFormColumns.sizeOfPropertyM2 ?? undefined,
        coverImageUrl: pictures?.[0]?.src,
        imageUrls: (pictures ?? []).map((p) => p.src),
        listingOrigin: "webot",
        ...(parsedListingContacts?.length ? { listingContacts: parsedListingContacts } : {})
      };

      // Awaited — see `onListingCreation`'s docs. This is what keeps the dialog
      // in its loading state until everything chained off the new listing (the
      // seller contact, the link back to it) has actually been written.
      //
      // Its own try/catch because the document ALREADY EXISTS by this point:
      // letting a follow-up write's failure fall into the outer catch would fire
      // `onError` — "failed to create listing" — for a listing that was created,
      // and the realtor would save again and get a second one.
      try {
        await onListingCreation?.(id, isExclusivitySet, createdListing);
      } catch (creationCallbackError) {
        console.error(
          'A listing-creation callback failed after the listing was written:',
          creationCallbackError
        );
      }
    } else {
      const { listing, editStatus } = operationProps;

      targetListing = {
        ...targetListing,
        ...(editStatus && { status: editStatus })
      };

      await firebase
        .firestore()
        .collection("real-estate-listings")
        .doc(listing?.id)
        .update(targetListing);
    }
  } catch (err) {
    console.error(err);
    onError();
    return;
  }

  // Outside the try on purpose: `onError` means "the write failed", and a caller
  // whose own success handler throws has not lost its listing. The caller is
  // responsible for catching that — `NewListingDialog.handleSubmit` does.
  await onSuccess(pictures);
}

/**
 * The address fields, in the shape a `real-estate-listings` document stores
 * them. Deliberately NOT `ProcessedListingAddress`: the CRM record keeps the
 * street name in `address`, the house number in its own `streetNumber` field,
 * and the floor / apartment number as top-level columns. `convertListingToProcessedListingData`
 * is what reassembles them into the nested `address` object everything else reads.
 */
export type ListingAddressWriteBack = {
  /** Street NAME only — the house number belongs in `streetNumber`. */
  address?: string;
  streetNumber?: number | null;
  apartmentNumber?: number | null;
  floor?: number | null;
  city?: string;
};

/**
 * Patch just the address fields of one listing, leaving every other column
 * untouched.
 *
 * Separate from `createOrUpdateListing` on purpose: that function rebuilds the
 * ENTIRE document from a fully-populated edit form (re-uploading images,
 * recomputing price history, rewriting owners). Routing a two-field address
 * correction through it would need the whole form state and would silently
 * clobber anything the caller didn't happen to hold. Callers must check the
 * user may edit the listing before calling — Firestore rules are the backstop,
 * not the gate.
 */
export async function updateListingAddressFields(
  listingId: string,
  fields: ListingAddressWriteBack
): Promise<void> {
  // Drop untouched keys so a partial patch can never blank a column it wasn't
  // asked about (Firestore treats an explicit `undefined` as an error anyway).
  const update = Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined)
  );
  if (Object.keys(update).length === 0) return;

  await firebase.firestore().collection("real-estate-listings").doc(listingId).update(update);
}
